import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  LogOut, LayoutDashboard, Users as UsersIcon, Building2, Church as ChurchIcon,
  UserSquare2, CalendarCheck, HeartHandshake, ScrollText, Plus, Trash2, KeyRound,
  Pencil, ShieldCheck, X, CalendarDays, Megaphone, CheckCircle, Inbox, MessagesSquare,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import AvatarUploader from "../components/AvatarUploader";
import CommunityFeed from "../components/CommunityFeed";
import NotificationsBell from "../components/NotificationsBell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = (token) => ({ headers: { Authorization: `Bearer ${token}` } });
const genPassword = () =>
  "EKG-" + Math.random().toString(36).slice(2, 8) + Math.floor(10 + Math.random() * 89);
const fieldCls =
  "bg-black border-0 border-b border-white/20 rounded-none focus-visible:ring-0 focus-visible:border-primary h-11 px-0 text-foreground font-body";

function Stat({ label, value }) {
  return (
    <div className="border border-border bg-muted/30 p-6">
      <p className="overline text-muted-foreground mb-3">{label}</p>
      <p className="font-display text-5xl text-foreground">{value}</p>
    </div>
  );
}

function DataTable({ cols, rows, keys, testid }) {
  return (
    <div className="overflow-x-auto border border-border" data-testid={testid}>
      <table className="w-full text-left">
        <thead>
          <tr className="border-b border-border">
            {cols.map((c) => (
              <th key={c} className="overline text-secondary text-[0.6rem] p-4">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={cols.length} className="p-6 text-muted-foreground font-body">No records yet.</td></tr>
          )}
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-border/60 hover:bg-muted/30">
              {keys.map((k) => (
                <td key={k} className="p-4 font-body text-sm text-foreground/90">
                  {k === "amount" ? `$${Number(r[k]).toFixed(2)}` : k === "created_at" ? String(r[k]).slice(0, 10) : (r[k] || "—")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function IconBtn({ onClick, title, children, testid }) {
  return (
    <button onClick={onClick} title={title} data-testid={testid}
      className="p-2 border border-border text-muted-foreground hover:text-secondary hover:border-secondary transition-colors">
      {children}
    </button>
  );
}

/* ---------- Generic CRUD directory section ---------- */
function CrudSection({ token, title, path, fields, columns, testid, typeOptions, nameKey = "name" }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const load = useCallback(() => {
    axios.get(`${API}/admin/${path}`, authHeader(token)).then((r) => setRows(r.data)).catch(() => {});
  }, [path, token]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm(Object.fromEntries(fields.map((f) => [f.key, f.input === "checkbox" ? false : (f.default || "")])));
    setOpen(true);
  };
  const openEdit = (row) => { setEditing(row); setForm({ ...row }); setOpen(true); };

  const save = async () => {
    if (!form[nameKey]) return toast.error("A title/name is required.");
    const payload = Object.fromEntries(fields.map((f) => {
      const v = form[f.key];
      if (f.input === "checkbox") return [f.key, !!v];
      return [f.key, v ?? (f.default ?? "")];
    }));
    try {
      if (editing) await axios.patch(`${API}/admin/${path}/${editing.id}`, payload, authHeader(token));
      else await axios.post(`${API}/admin/${path}`, payload, authHeader(token));
      toast.success("Saved.");
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong."); }
  };
  const del = async (row) => {
    if (!window.confirm(`Delete "${row[nameKey]}"? This cannot be undone.`)) return;
    try { await axios.delete(`${API}/admin/${path}/${row.id}`, authHeader(token)); toast.success("Deleted."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const renderField = (f) => {
    if (f.input === "textarea") {
      return <textarea rows={4} value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
        className={`${fieldCls} w-full h-auto py-2 resize-none`} data-testid={`${testid}-field-${f.key}`} />;
    }
    if (f.input === "checkbox") {
      return (
        <label className="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" checked={!!form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.checked })}
            className="accent-[hsl(357_73%_31%)] w-4 h-4" data-testid={`${testid}-field-${f.key}`} />
          <span className="font-body text-sm text-foreground">{f.checkboxLabel || "Enable"}</span>
        </label>
      );
    }
    const opts = f.options || (f.key === "type" ? typeOptions : null);
    if (opts) {
      return (
        <select value={form[f.key] || f.default} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
          className={`${fieldCls} w-full`} data-testid={`${testid}-field-${f.key}`}>
          {opts.map((o) => <option key={o} value={o} className="bg-black">{o}</option>)}
        </select>
      );
    }
    return <Input type={f.input === "date" ? "date" : "text"} value={form[f.key] || ""}
      onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className={fieldCls} data-testid={`${testid}-field-${f.key}`} />;
  };

  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display uppercase text-3xl md:text-4xl text-foreground">{title}</h2>
        <button onClick={openNew} data-testid={`${testid}-add`}
          className="flex items-center gap-2 bg-primary text-primary-foreground overline px-5 py-3 hover:bg-primary/85 transition-colors">
          <Plus size={16} /> Add
        </button>
      </div>
      <div className="overflow-x-auto border border-border">
        <table className="w-full text-left">
          <thead><tr className="border-b border-border">
            {columns.map((c) => <th key={c.key} className="overline text-secondary text-[0.6rem] p-4">{c.label}</th>)}
            <th className="overline text-secondary text-[0.6rem] p-4 text-right">Actions</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={columns.length + 1} className="p-6 text-muted-foreground font-body">No records yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-border/60 hover:bg-muted/30">
                {columns.map((c) => <td key={c.key} className="p-4 font-body text-sm text-foreground/90">{typeof r[c.key] === "boolean" ? (r[c.key] ? "Yes" : "—") : (r[c.key] || "—")}</td>)}
                <td className="p-4">
                  <div className="flex items-center gap-2 justify-end">
                    <IconBtn onClick={() => openEdit(r)} title="Edit" testid={`${testid}-edit`}><Pencil size={14} /></IconBtn>
                    <IconBtn onClick={() => del(r)} title="Delete" testid={`${testid}-del`}><Trash2 size={14} /></IconBtn>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0c0c0c] border-border rounded-none max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display uppercase text-2xl text-foreground">{editing ? "Edit" : "New"} {title}</DialogTitle>
            <DialogDescription className="sr-only">Enter the {title} details and save.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {fields.map((f) => (
              <div key={f.key}>
                {f.input !== "checkbox" && <label className="overline text-muted-foreground block mb-2 text-[0.6rem]">{f.label}</label>}
                {renderField(f)}
              </div>
            ))}
          </div>
          <DialogFooter>
            <button onClick={save} data-testid={`${testid}-save`}
              className="bg-primary text-primary-foreground overline px-6 py-3 hover:bg-primary/85 transition-colors">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------- Users section ---------- */
function UsersSection({ token, isSuper, meta }) {
  const [rows, setRows] = useState([]);
  const [churches, setChurches] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const initForm = { name: "", email: "", title: "", roles: [], password: genPassword(), is_admin: false, status: "active", access_role: "member", church_id: "" };
  const [form, setForm] = useState(initForm);

  const load = useCallback(() => {
    axios.get(`${API}/admin/users`, authHeader(token)).then((r) => setRows(r.data)).catch(() => {});
    axios.get(`${API}/admin/churches`, authHeader(token)).then((r) => setChurches(r.data)).catch(() => {});
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm({ ...initForm, password: genPassword() }); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ ...u, access_role: u.role || "member", church_id: u.church_id || "" }); setOpen(true); };
  const toggleRole = (role) => setForm((f) => ({ ...f, roles: f.roles.includes(role) ? f.roles.filter((r) => r !== role) : [...f.roles, role] }));

  const save = async () => {
    if (!form.name || !form.email) return toast.error("Name and email are required.");
    try {
      if (editing) {
        await axios.patch(`${API}/admin/users/${editing.id}`, {
          name: form.name, title: form.title, roles: form.roles, status: form.status,
          role: form.access_role, church_id: form.church_id || "",
          ...(isSuper ? { is_admin: form.is_admin } : {}),
        }, authHeader(token));
        toast.success("User updated.");
      } else {
        await axios.post(`${API}/admin/users`, {
          name: form.name, email: form.email, title: form.title, roles: form.roles,
          password: form.password, is_admin: form.is_admin,
          role: form.access_role, church_id: form.church_id || "",
        }, authHeader(token));
        toast.success(`User created. Temp password: ${form.password}`, { duration: 8000 });
      }
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const resetPw = async (u) => {
    const pw = genPassword();
    try { await axios.post(`${API}/admin/users/${u.id}/reset-password`, { password: pw }, authHeader(token)); toast.success(`New temp password for ${u.name}: ${pw}`, { duration: 8000 }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const toggleStatus = async (u) => {
    const status = u.status === "suspended" ? "active" : "suspended";
    try { await axios.patch(`${API}/admin/users/${u.id}`, { status }, authHeader(token)); toast.success(`User ${status}.`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const del = async (u) => {
    if (!window.confirm(`Delete ${u.name}?`)) return;
    try { await axios.delete(`${API}/admin/users/${u.id}`, authHeader(token)); toast.success("Deleted."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  return (
    <div data-testid="users-section">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display uppercase text-3xl md:text-4xl text-foreground">Users</h2>
        <button onClick={openNew} data-testid="users-add"
          className="flex items-center gap-2 bg-primary text-primary-foreground overline px-5 py-3 hover:bg-primary/85 transition-colors">
          <Plus size={16} /> Add User
        </button>
      </div>
      <div className="overflow-x-auto border border-border">
        <table className="w-full text-left">
          <thead><tr className="border-b border-border">
            {["Name", "Email", "Roles", "Status", ""].map((c, i) => <th key={i} className="overline text-secondary text-[0.6rem] p-4">{c}</th>)}
          </tr></thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.id} className="border-b border-border/60 hover:bg-muted/30 align-top">
                <td className="p-4 font-body text-sm text-foreground/90">
                  <span className="flex items-center gap-2">{u.name}
                    {u.is_super_admin ? <ShieldCheck size={14} className="text-secondary" title="Super Admin" />
                      : u.is_admin ? <ShieldCheck size={13} className="text-primary" title="Admin" /> : null}
                  </span>
                  <span className="block text-muted-foreground text-xs">{u.title}</span>
                </td>
                <td className="p-4 font-body text-sm text-foreground/70">{u.email}</td>
                <td className="p-4 font-body text-xs text-muted-foreground max-w-xs">{(u.roles || []).join(", ") || "—"}</td>
                <td className="p-4"><span className={`overline text-[0.6rem] ${u.status === "suspended" ? "text-primary" : "text-secondary"}`}>{u.status}</span></td>
                <td className="p-4">
                  <div className="flex items-center gap-2 justify-end">
                    <IconBtn onClick={() => openEdit(u)} title="Edit"><Pencil size={14} /></IconBtn>
                    <IconBtn onClick={() => resetPw(u)} title="Reset password"><KeyRound size={14} /></IconBtn>
                    {!u.is_super_admin && <IconBtn onClick={() => toggleStatus(u)} title="Suspend / activate"><X size={14} /></IconBtn>}
                    {!u.is_super_admin && <IconBtn onClick={() => del(u)} title="Delete"><Trash2 size={14} /></IconBtn>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0c0c0c] border-border rounded-none max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display uppercase text-2xl text-foreground">{editing ? "Edit User" : "New User"}</DialogTitle>
            <DialogDescription className="sr-only">Manage user details, roles, and access.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input placeholder="Full Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={fieldCls} data-testid="user-name" />
              <Input placeholder="Email" type="email" value={form.email} disabled={!!editing}
                onChange={(e) => setForm({ ...form, email: e.target.value })} className={`${fieldCls} disabled:opacity-50`} data-testid="user-email" />
              <Input placeholder="Title (e.g. Pastor)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={fieldCls} data-testid="user-title" />
              {!editing && (
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <label className="overline text-muted-foreground block mb-2 text-[0.6rem]">Temp Password</label>
                    <Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={fieldCls} data-testid="user-password" />
                  </div>
                  <button type="button" onClick={() => setForm({ ...form, password: genPassword() })}
                    className="overline text-secondary border border-secondary px-3 py-2 text-[0.6rem]">New</button>
                </div>
              )}
            </div>

            {editing && (
              <div>
                <label className="overline text-muted-foreground block mb-2 text-[0.6rem]">Status</label>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={`${fieldCls} w-full`}>
                  <option value="active" className="bg-black">active</option>
                  <option value="suspended" className="bg-black">suspended</option>
                </select>
              </div>
            )}

            {isSuper && (
              <label className="flex items-center gap-3 cursor-pointer" data-testid="user-admin-toggle">
                <input type="checkbox" checked={!!form.is_admin} onChange={(e) => setForm({ ...form, is_admin: e.target.checked })} className="accent-[hsl(357_73%_31%)] w-4 h-4" />
                <span className="font-body text-sm text-foreground">Grant Administrator access</span>
              </label>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="overline text-muted-foreground block mb-2 text-[0.6rem]">Access Role (login)</label>
                <select value={form.access_role} onChange={(e) => setForm({ ...form, access_role: e.target.value })} className={`${fieldCls} w-full`} data-testid="user-access-role">
                  <option value="member" className="bg-black">member</option>
                  <option value="staff" className="bg-black">staff</option>
                  <option value="church_leader" className="bg-black">church_leader</option>
                  {isSuper && <option value="admin" className="bg-black">admin</option>}
                  {isSuper && <option value="super_admin" className="bg-black">super_admin</option>}
                </select>
              </div>
              <div>
                <label className="overline text-muted-foreground block mb-2 text-[0.6rem]">Assigned Church</label>
                <select value={form.church_id} onChange={(e) => setForm({ ...form, church_id: e.target.value })} className={`${fieldCls} w-full`} data-testid="user-church-select">
                  <option value="" className="bg-black">— None —</option>
                  {churches.map((c) => <option key={c.id} value={c.id} className="bg-black">{c.name} · {c.city || c.state}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="overline text-secondary block mb-3 text-[0.6rem]">Assign Roles</label>
              {meta && Object.entries(meta.role_catalog).map(([group, roles]) => (
                <div key={group} className="mb-4">
                  <p className="overline text-muted-foreground text-[0.55rem] mb-2">{group}</p>
                  <div className="flex flex-wrap gap-2">
                    {roles.map((role) => (
                      <button key={role} type="button" onClick={() => toggleRole(role)}
                        className={`overline text-[0.6rem] px-3 py-2 border transition-colors ${form.roles.includes(role) ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-secondary"}`}>
                        {role}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <button onClick={save} data-testid="user-save"
              className="bg-primary text-primary-foreground overline px-6 py-3 hover:bg-primary/85 transition-colors">Save User</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------- Read-only section ---------- */
function ReadOnly({ token, title, path, cols, keys, testid, transform }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    axios.get(`${API}/admin/${path}`, authHeader(token)).then((r) => setRows(transform ? transform(r.data) : r.data)).catch(() => {});
  }, [token, path, transform]);
  return (
    <div data-testid={testid}>
      <h2 className="font-display uppercase text-3xl md:text-4xl text-foreground mb-6">{title}</h2>
      <DataTable testid={`${testid}-table`} cols={cols} keys={keys} rows={rows} />
    </div>
  );
}

function ApprovalsSection({ token }) {
  const [data, setData] = useState({ users: [], events: [], announcements: [] });
  const load = useCallback(() => {
    axios.get(`${API}/admin/pending`, authHeader(token))
      .then((r) => setData(r.data)).catch(() => {});
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const approveUser = async (id) => {
    try {
      await axios.post(`${API}/admin/users/${id}/approve`, {}, authHeader(token));
      toast.success("User approved & activated.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
  };
  const approveItem = async (kind, id) => {
    try {
      await axios.post(`${API}/admin/${kind}/${id}/approve`, {}, authHeader(token));
      toast.success("Approved & published.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
  };

  const empty = !data.users.length && !data.events.length && !data.announcements.length;

  return (
    <div data-testid="approvals-section" className="space-y-10">
      {empty && (
        <p className="font-editorial italic text-2xl text-muted-foreground border border-border p-10 text-center">
          Nothing pending. All caught up.
        </p>
      )}
      {data.users.length > 0 && (
        <div>
          <h2 className="font-display uppercase text-2xl text-foreground mb-4">Pending Sign-Ups ({data.users.length})</h2>
          <div className="border border-border">
            {data.users.map((u) => (
              <div key={u.id} className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 px-4 py-3 border-b border-border" data-testid="pending-user-row">
                <div>
                  <p className="font-body text-foreground">{u.name}</p>
                  <p className="font-body text-xs text-muted-foreground">{u.email}</p>
                </div>
                <button onClick={() => approveUser(u.id)} data-testid={`approve-user-${u.id}`}
                  className="flex items-center gap-2 overline text-[0.65rem] bg-secondary text-background px-4 py-2 hover:bg-secondary/85 transition-colors">
                  <CheckCircle size={14} /> Approve
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      {data.events.length > 0 && (
        <div>
          <h2 className="font-display uppercase text-2xl text-foreground mb-4">Pending Events ({data.events.length})</h2>
          <div className="border border-border">
            {data.events.map((e) => (
              <div key={e.id} className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 px-4 py-3 border-b border-border" data-testid="pending-event-row">
                <div>
                  <p className="font-body text-foreground">{e.title}</p>
                  <p className="font-body text-xs text-muted-foreground">{e.date} · {e.location} · {e.church_name}</p>
                </div>
                <button onClick={() => approveItem("events", e.id)} data-testid={`approve-event-${e.id}`}
                  className="flex items-center gap-2 overline text-[0.65rem] bg-secondary text-background px-4 py-2 hover:bg-secondary/85 transition-colors">
                  <CheckCircle size={14} /> Publish
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      {data.announcements.length > 0 && (
        <div>
          <h2 className="font-display uppercase text-2xl text-foreground mb-4">Pending Announcements ({data.announcements.length})</h2>
          <div className="border border-border">
            {data.announcements.map((a) => (
              <div key={a.id} className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 px-4 py-3 border-b border-border" data-testid="pending-announcement-row">
                <div>
                  <p className="font-body text-foreground">{a.title}</p>
                  <p className="font-body text-xs text-muted-foreground">{a.church_name}</p>
                </div>
                <button onClick={() => approveItem("announcements", a.id)} data-testid={`approve-announcement-${a.id}`}
                  className="flex items-center gap-2 overline text-[0.65rem] bg-secondary text-background px-4 py-2 hover:bg-secondary/85 transition-colors">
                  <CheckCircle size={14} /> Publish
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const NAV = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "community", label: "Community", icon: MessagesSquare },
  { key: "approvals", label: "Approvals", icon: Inbox },
  { key: "users", label: "Users", icon: UsersIcon },
  { key: "organizations", label: "Organizations", icon: Building2 },
  { key: "churches", label: "Churches", icon: ChurchIcon },
  { key: "members", label: "Members", icon: UserSquare2 },
  { key: "events", label: "Events", icon: CalendarDays },
  { key: "announcements", label: "Announcements", icon: Megaphone },
  { key: "registrations", label: "Registrations", icon: CalendarCheck },
  { key: "prayers", label: "Prayer Requests", icon: HeartHandshake },
  { key: "audit", label: "Audit Log", icon: ScrollText },
];

export default function AdminDashboard() {
  const { admin, token, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [section, setSection] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [meta, setMeta] = useState(null);
  const isSuper = admin && admin.role === "super_admin";

  useEffect(() => { if (admin === false) navigate("/admin/login"); }, [admin, navigate]);

  useEffect(() => {
    const t = searchParams.get("tab");
    if (t) setSection(t);
  }, [searchParams]);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/admin/overview`, authHeader(token)).then((r) => setOverview(r.data)).catch(() => {});
    axios.get(`${API}/admin/roles`, authHeader(token)).then((r) => setMeta(r.data)).catch(() => {});
  }, [token, section]);

  if (!admin) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;

  return (
    <div className="min-h-screen flex flex-col md:flex-row" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="md:w-64 md:min-h-screen border-b md:border-b-0 md:border-r border-border bg-[#080808] md:sticky md:top-0 md:h-screen flex flex-col">
        <div className="p-6 border-b border-border">
          <p className="overline text-primary text-[0.6rem] mb-1">EKG Platform</p>
          <p className="font-display uppercase text-lg text-foreground leading-none">Management</p>
        </div>
        <div className="p-5 border-b border-border flex items-center gap-3">
          <AvatarUploader size={44} />
          <div className="min-w-0 flex-1">
            <p className="font-body text-sm text-foreground truncate" data-testid="admin-user-name">{admin.name || admin.email}</p>
            <p className="overline text-secondary text-[0.55rem]">{admin.role?.replace("_", " ")}</p>
          </div>
          <NotificationsBell target="/admin" />
        </div>
        <nav className="flex md:flex-col overflow-x-auto p-3 gap-1 flex-1">
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <button key={n.key} onClick={() => setSection(n.key)} data-testid={`nav-${n.key}`}
                className={`flex items-center gap-3 px-4 py-3 overline text-[0.65rem] whitespace-nowrap transition-colors ${section === n.key ? "bg-primary/15 text-secondary border-l-2 border-primary" : "text-muted-foreground hover:text-foreground"}`}>
                <Icon size={16} /> {n.label}
              </button>
            );
          })}
        </nav>
        <div className="p-3 border-t border-border">
          <button onClick={() => { logout(); navigate("/admin/login"); }} data-testid="admin-logout"
            className="flex items-center gap-3 px-4 py-3 overline text-[0.65rem] text-muted-foreground hover:text-primary transition-colors w-full">
            <LogOut size={16} /> Logout
          </button>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 p-6 md:p-12 max-w-[1400px]">
        <div className="mb-10">
          <p className="overline text-muted-foreground text-[0.6rem] mb-2">Signed in as {admin.email}</p>
          <h1 className="font-display uppercase text-3xl md:text-5xl text-foreground">
            {NAV.find((n) => n.key === section)?.label}
          </h1>
        </div>

        {section === "overview" && (
          <div>
            {overview && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Stat label="Organizations" value={overview.organizations} />
                <Stat label="Users" value={overview.users} />
                <Stat label="Churches" value={overview.churches} />
                <Stat label="Members" value={overview.members} />
                <Stat label="Registrations" value={overview.registrations} />
                <Stat label="Prayer Requests" value={overview.prayers} />
                <Stat label="Total Given" value={`$${Number(overview.donations_total).toFixed(0)}`} />
              </div>
            )}
          </div>
        )}

        {section === "users" && <UsersSection token={token} isSuper={isSuper} meta={meta} />}

        {section === "community" && (
          <div data-testid="admin-community">
            <p className="font-body text-sm text-muted-foreground mb-6">
              Full moderation view — hidden posts and comments are visible here for review. Pin, hide, or delete anything.
            </p>
            <CommunityFeed moderationMode={true} />
          </div>
        )}

        {section === "approvals" && <ApprovalsSection token={token} />}

        {section === "organizations" && (
          <CrudSection token={token} title="Organizations" path="organizations" testid="orgs"
            typeOptions={meta?.org_types}
            fields={[
              { key: "name", label: "Name" }, { key: "type", label: "Type", default: "Local Church" },
              { key: "region", label: "Region" }, { key: "city", label: "City" },
              { key: "state", label: "State" }, { key: "description", label: "Description" },
            ]}
            columns={[{ key: "name", label: "Name" }, { key: "type", label: "Type" }, { key: "city", label: "City" }, { key: "state", label: "State" }]} />
        )}

        {section === "churches" && (
          <CrudSection token={token} title="Churches" path="churches" testid="churches"
            fields={[
              { key: "name", label: "Church Name" }, { key: "pastor", label: "Lead Pastor" },
              { key: "city", label: "City" }, { key: "state", label: "State" },
              { key: "email", label: "Email" }, { key: "phone", label: "Phone" },
            ]}
            columns={[{ key: "name", label: "Church" }, { key: "pastor", label: "Pastor" }, { key: "city", label: "City" }, { key: "state", label: "State" }]} />
        )}

        {section === "members" && (
          <CrudSection token={token} title="Members" path="members" testid="members"
            fields={[
              { key: "name", label: "Name" }, { key: "email", label: "Email" },
              { key: "phone", label: "Phone" }, { key: "church", label: "Church" }, { key: "role", label: "Role", default: "Member" },
            ]}
            columns={[{ key: "name", label: "Name" }, { key: "email", label: "Email" }, { key: "church", label: "Church" }, { key: "role", label: "Role" }]} />
        )}

        {section === "events" && (
          <CrudSection token={token} title="Events" path="events" testid="events" nameKey="title"
            fields={[
              { key: "title", label: "Event Title" },
              { key: "type", label: "Type", input: "select", options: ["Service", "ALU Class", "Regional Gathering", "Conference", "Other"], default: "Service" },
              { key: "date", label: "Date", input: "date" },
              { key: "time", label: "Time (e.g. 7:00 PM)" },
              { key: "location", label: "Location" },
              { key: "description", label: "Description", input: "textarea" },
            ]}
            columns={[{ key: "title", label: "Event" }, { key: "type", label: "Type" }, { key: "date", label: "Date" }, { key: "location", label: "Location" }]} />
        )}

        {section === "announcements" && (
          <CrudSection token={token} title="Announcements" path="announcements" testid="announcements" nameKey="title"
            fields={[
              { key: "title", label: "Headline" },
              { key: "audience", label: "Audience", input: "select", options: ["Everyone", "Leaders", "Students", "Members"], default: "Everyone" },
              { key: "body", label: "Message", input: "textarea" },
              { key: "pinned", label: "Pinned", input: "checkbox", checkboxLabel: "Pin to top of the site" },
            ]}
            columns={[{ key: "title", label: "Headline" }, { key: "audience", label: "Audience" }, { key: "pinned", label: "Pinned" }]} />
        )}

        {section === "registrations" && (
          <ReadOnly token={token} title="Summit Registrations" path="registrations" testid="regs"
            cols={["Name", "Email", "Office", "Church", "Date"]} keys={["name", "email", "role", "church", "created_at"]} />
        )}

        {section === "prayers" && (
          <ReadOnly token={token} title="Prayer Requests" path="prayer-requests" testid="prayers"
            cols={["Name", "Email", "Type", "Message", "Date"]} keys={["name", "email", "request_type", "message", "created_at"]} />
        )}

        {section === "audit" && (
          <ReadOnly token={token} title="Audit Log" path="audit-logs" testid="audit"
            cols={["Actor", "Action", "Target", "Details", "Date"]} keys={["actor", "action", "target_name", "details", "created_at"]} />
        )}
      </main>
    </div>
  );
}
